import {
  faAddressBook,
  faPenToSquare,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { FolderTabs } from '@/common/FolderTabs';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { DeletePhotoTab } from '@/photo/DeletePhotoTab';
import { UploadPhotoTab } from '@/photo/UploadPhotoTab';
import { TakePhotoTab } from '@/profile/TakePhotoTab';
import { PageContent, PageHeader } from '@/settings/settingsStyles';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/profile/change-avatar',
)({
  component: EditProfilePhotoRoute,
});

function EditProfilePhotoRoute() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'upload' | 'takePhoto' | 'delete'>(
    'upload',
  );
  // TODO: implement camera permission handling
  const [cameraAllowed, _setCameraAllowed] = useState(true);
  const { me } = useCurrentUser();
  const hasPhoto = Boolean(me?.avatar);

  const photoTabs: {
    id: 'upload' | 'takePhoto' | 'delete';
    label: string;
  }[] = [
    { id: 'upload', label: t('wbs.upload') },
    { id: 'takePhoto', label: t('wbs.take-photo') },
    ...(hasPhoto
      ? [{ id: 'delete' as const, label: t('actions.delete') }]
      : []),
  ];

  const { memberId } = Route.useParams();

  const mainTabs = [
    {
      title: t('actions.edit'),
      to: 'edit',
      icon: faPenToSquare,
    },
    {
      title: t('profile.profile-photo'),
      to: 'change-avatar',
      icon: faAddressBook,
    },
  ];

  return (
    <PageContent>
      <PageHeader>
        <TitleWithQuestion title={t('actions.edit-profile')} />
        <Link to="/members/$memberId/profile/" params={{ memberId }}>
          <WBSButton wide alternate icon={faUser}>
            {t('profile.view-my-profile')}
          </WBSButton>
        </Link>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar tabs={mainTabs} base="/members/$memberId/profile" />
      </div>
      <InfoSectionContainer>
        <FormTitle>{t('profile.change-profile-photo')}</FormTitle>

        <AlertBox variant="info">
          {t('profile.your-profile-photo-will')}
        </AlertBox>
        <OptionsContainer>
          <FolderTabs
            tabs={photoTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <div style={{ marginTop: '20px' }}>
            {activeTab === 'upload' && <UploadPhotoTab />}
            {activeTab === 'takePhoto' && (
              <TakePhotoTab cameraAllowed={cameraAllowed} />
            )}
            {activeTab === 'delete' && hasPhoto && <DeletePhotoTab />}
          </div>
        </OptionsContainer>
      </InfoSectionContainer>
    </PageContent>
  );
}

const OptionsContainer = styled.div`
  border-radius: 5px;
  box-shadow: var(--shadow-md);
  margin-top: 32px;
  padding: 50px 20px 65px;
`;

const FormTitle = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 22px;
  font-weight: 400;
  letter-spacing: 0.1px;
  margin: 5px 0 26px 0;
`;
